<template>
  <div class="form-input-element-wrapper">
    <FormLabel
      :element="element"
      :labelStyle="labelStyle"
      :fieldStyle="fieldStyle"
    />
    <FormDescription :element="element" :labelStyle="labelStyle" />
    <div
      class="input-field-wrapper position-relative"
      @dragover.prevent="onDragOver"
      @dragleave="onDragLeave"
      @drop.prevent="onDrop"
    >
      <div
        class="form-input-element"
        :class="{
          'drag-over': isDragOver,
          'has-file': isFileAdded,
          'has-multiple': isAllowedMultiple,
        }"
        :style="fieldStyle"
      >
        <div
          class="add-btn"
          v-if="!isFileAdded || isAllowedMultiple"
          @click="toggleFileInput"
        >
          <svg
            v-if="!isLoading"
            width="17"
            height="17"
            viewBox="0 0 17 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8.20721 1V15.4145M0.99997 8.20724H15.4145"
              stroke="#333343"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>

          <!-- loading spinner -->
          <LoadingSpinner v-else />
        </div>
        <div class="file-info-wrapper">
          <div class="upload-placeholder" v-if="!isFileAdded">
            <span :style="brandTextStyle"
              >Datei hierher ziehen oder hochladen</span
            >
          </div>
          <template v-if="isFileAdded">
            <div
              class="file-chip"
              v-for="(file, index) in this.element.files"
              :key="index"
            >
              <span class="file-chip-label" :style="brandTextStyle">{{
                file.name
              }}</span>
              <div
                class="file-chip-remove"
                @click="onRemoveFile($event, index)"
              >
                <b-icon icon="x-circle-fill"></b-icon>
              </div>
            </div>
          </template>
        </div>
        <input
          v-show="!isFileAdded"
          class="form-input fw-normal fs-6 w-100 bg-transparent"
          :class="{
            'user-select-none': isDisabled,

            'prevent-pointer': isFileAdded,
          }"
          :id="`input-${element.id}`"
          :name="element.name"
          type="file"
          :required="isRequired"
          :disabled="isDisabled"
          ref="fileInput"
          :multiple="isAllowedMultiple"
          @change="onHandleChange"
          @dragover.prevent="onDragOver"
          @dragleave="onDragLeave"
        />
        <div
          v-if="element?.settings?.prefixIcon?.icon"
          class="prefix-image-wrap position-absolute overflow-hidden bg-transparent d-flex align-items-center justify-content-center"
        >
          <img
            class="prefix-icon object-fit-contain mh-100 mw-100"
            :id="`prefix-icon-${element.id}`"
            :src="element.settings.prefixIcon.icon"
          />
        </div>
        <img
          class="error-icon position-absolute top-50"
          :src="require('@/assets/img/error-circle-exclamation.svg')"
        />
      </div>
    </div>
  </div>
</template>

<script>
import FormLabel from '@/components/FormBuilder/core/FormLabel.vue';
import FormDescription from '@/components/FormBuilder/core/FormDescription.vue';
import LoadingSpinner from '@/components/widgets/LoadingSpinner.vue';

export default {
  name: 'FormBuilderFileElement',
  props: {
    element: {
      type: Object,
      required: true,
    },
    value: {
      type: [String, Number, Boolean],
      default: '',
    },
    labelStyle: { type: Object, default: () => {} },
    fieldStyle: { type: Object, default: () => {} },
  },
  data() {
    return {
      isDragOver: false,
      isLoading: false,
      isAllowedMultiple: false,
    };
  },
  methods: {
    setInputFiles(files) {
      if (this.element.files && !files?.length) {
        return;
      }
      const dataTransfer = new DataTransfer();

      if (!this.isAllowedMultiple) {
        dataTransfer.items.add(files[0]);
      } else {
        const existingFiles = Array.from(this.element.files || []);
        const newFiles = Array.from(files);
        const combinedFiles = [...existingFiles, ...newFiles];

        combinedFiles.forEach((file) => dataTransfer.items.add(file));
      }

      this.$refs.fileInput.files = dataTransfer.files;
      this.$set(this.element, 'files', dataTransfer.files);
    },
    onHandleChange(event) {
      this.setInputFiles(event.target.files);
    },

    onDragOver() {
      this.isDragOver = true;
    },
    onDragLeave(event) {
      const rect = event.currentTarget.getBoundingClientRect();
      const { clientX, clientY } = event;

      if (
        clientX < rect.left ||
        clientX >= rect.right ||
        clientY < rect.top ||
        clientY >= rect.bottom
      ) {
        this.isDragOver = false;
      }
    },
    onDrop(event) {
      this.isDragOver = false;
      const droppedFiles = event.dataTransfer.files;
      this.setInputFiles(droppedFiles);
    },
    onRemoveFile(event, index) {
      event.stopPropagation();

      const updatedFiles = Array.from(this.element.files);
      updatedFiles.splice(index, 1);

      const dataTransfer = new DataTransfer();
      updatedFiles.forEach((file) => dataTransfer.items.add(file));

      // Update both the input element and the Vue reactive property
      this.$refs.fileInput.files = dataTransfer.files;
      this.$set(this.element, 'files', dataTransfer.files);
    },
    toggleFileInput() {
      if (this.isLoading) return;
      this.$refs.fileInput.click();
    },
  },
  getPlaceholder() {
    return this.element.settings.placeholder || '';
  },
  computed: {
    isRequired() {
      return this.element.settings.required || false;
    },
    isDisabled() {
      return this.element.settings.disabled;
    },
    isFileAdded() {
      return !!this.element?.files?.length;
    },
    brandTextStyle() {
      return {
        color: this.fieldStyle?.color || '',
      };
    },
  },

  components: {
    FormLabel,
    FormDescription,
    LoadingSpinner,
  },
};
</script>

<style scoped>
.form-input-element-wrapper:has(input[type='hidden']) .form-label,
.form-input-element-wrapper:has(.form-description) .form-label {
  margin-bottom: 9px;
}

.form-input-element-wrapper .input-field-wrapper img.error-icon {
  right: 25px;
  transform: translateY(-50%);
  cursor: pointer;
  color: #ff0000;
  width: 21px;
  height: 21px;
  display: none;
  user-drag: none;
  -webkit-user-drag: none;
}

.form-input-element-wrapper .input-field-wrapper .prefix-image-wrap {
  width: 30px;
  height: 30px;
  left: 20px;
  top: 25px;
}

.form-input-element-wrapper .prefix-image-wrap img.prefix-icon {
  -webkit-user-drag: none;
}
.form-input-element-wrapper:has(.prefix-icon) .form-input-element {
  padding-left: 60px !important;
}
.was-submitted
  .form-input-element-wrapper:has(.form-input:invalid)
  img.error-icon {
  display: block;
}

.form-input-element-wrapper .form-input {
  font-family: 'OpenSans-Regular';
  border: 0.7px solid #dddddd;
  border-radius: 10px;
  letter-spacing: -0.02em;
  color: #222222;
  cursor: auto;
  vertical-align: middle;
  /* padding: 16px !important; */
  line-height: 27px !important;
  /* z-index: -1; */
}

.form-input-element-wrapper .drag-over {
  background-color: #d9d9d9;
  opacity: 0.4;
}

.form-input-element-wrapper .selected-file-list {
  height: 65px;
  border: 0.7px solid #dddddd;
  border-radius: 10px;
  /* padding: 16px !important; */
}
.form-input-element-wrapper .remove-icon {
  background: black;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  text-align: center;
  color: white;
  cursor: pointer;
  transition: 0.5s ease all;
}
.was-submitted .form-input-element:has(.form-input:invalid) {
  outline: 2.2px solid #ff0000 !important;
  background-color: #ffffff;
  border-color: transparent !important;
  outline-offset: -2.2px;
}

.form-input-element-wrapper .form-input-element {
  min-height: 80px;
  border: 0.7px solid #dddddd;
  border-radius: 10px;
  padding: 8px;

  display: flex;
  align-items: center;
}

.form-input-element-wrapper .add-btn {
  height: calc(100% - 16px);
  min-height: 64px;
  min-width: 65px;
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  position: absolute;
  left: 8px;
  font-size: 22px;
  z-index: 1;
  color: #000;
}
.form-input-element-wrapper:has(.prefix-icon) .add-btn {
  margin-left: 60px !important;
}
.form-input-element-wrapper .form-input-element:hover .add-btn {
  background-color: #f5f5f5;
}
.form-input-element-wrapper .form-input-element:hover {
  outline: 2.2px solid #000000;
  outline-offset: -2.2px;
}
.form-input-element-wrapper .form-input-element .add-btn:has(.loading) {
  background-color: #f5f5f5;
}

.form-input-element-wrapper .file-info-wrapper {
  margin-left: 16px;
}

.form-input-element-wrapper .form-input {
  opacity: 0;
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
}
.form-input-element-wrapper .form-input::-webkit-file-upload-button {
  visibility: hidden;
}

.form-input-element-wrapper .file-chip {
  display: flex;
  gap: 10px;
  border: 1px solid #1c1c1c;
  border-radius: 11px;
  padding: 5px 10px;
  align-items: center;
  min-height: 39px;
}

.form-input-element-wrapper .file-chip .file-chip-label {
  word-break: break-all;
  font-family: 'OpenSans-Regular';
  font-weight: 400;
  font-size: 16px;
  letter-spacing: -0.02em;
  color: #000;
}
.form-input-element-wrapper .file-chip-remove {
  font-size: 24px;
  cursor: pointer;
  line-height: 1;
  color: #000;
}
.form-input-element-wrapper .file-info-wrapper {
  margin-left: 16px;
  display: flex;
  gap: 6px 7px;
  flex-wrap: wrap;
  width: calc(100% - 80px);
  max-width: 100%;
  margin-left: auto;
}
.form-input-element-wrapper:has(.prefix-icon) .file-info-wrapper {
  width: calc(100% - 80px - 8px);
}
.form-input-element-wrapper:has(.has-file):not(:has(.has-multiple)):not(
    :has(.prefix-icon)
  )
  .file-info-wrapper {
  width: calc(100% - 8px - 9px);
  padding-block: 9px;
  margin-right: 9px;
}
.form-input-element-wrapper:has(.has-file):has(.prefix-icon):not(
    :has(.has-multiple)
  )
  .file-info-wrapper {
  width: calc(100% - 8px - 9px);
  padding-block: 9px;
  margin-right: 9px;
}
.form-input-element-wrapper:has(.has-file):has(.prefix-icon):not(
    :has(.has-multiple)
  )
  .file-info-wrapper {
  width: calc(100% - 8px - 9px);
  padding-block: 9px;
  margin-right: 9px;
}
.form-input-element-wrapper:has(.has-file):has(.has-multiple):not(
    :has(.prefix-icon)
  )
  .file-info-wrapper {
  width: calc(100% - 80px - 9px);
  padding-block: 9px;
  margin-right: 9px;
}
.form-input-element-wrapper:has(.has-file):has(.has-multiple)
  :has(.prefix-icon)
  .file-info-wrapper {
  width: calc(100% - 80px - 8px - 9px);
  padding-block: 9px;
  margin-right: 9px;
}

.form-input-element-wrapper .prevent-pointer {
  pointer-events: none;
}

.form-input-element-wrapper .upload-placeholder {
  font-family: 'OpenSans-Regular';
  font-weight: 400;
  font-size: 16px;
  letter-spacing: -0.02em;
  color: #000;
}

/* loader styles */
.form-input-element-wrapper .progress-icon {
  position: absolute !important;
}
.form-input-element-wrapper ::v-deep .progress-icon {
  width: 36px;
  height: 36px;
  position: static !important;
  transform: none !important;
}
.form-input-element-wrapper ::v-deep .progress-icon:after {
  width: 36px;
  height: 36px;
  border-width: 1.5px;
  border-color: #8c8c8c transparent !important;
}

.form-input-element-wrapper ::v-deep .progress-icon-wrapper {
  top: unset;
}

.form-input-element-wrapper ::v-deep .loading {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: transparent;
}

.form-input-element-wrapper ::v-deep .loading .progress-icon-wrapper {
  position: static;
  transform: none !important;
}
</style>
